<template>
  <v-container fluid class="pa-6">
    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-center align-center" style="min-height: 400px;">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </div>

    <!-- Error State -->
    <v-alert
      v-else-if="error"
      type="error"
      class="mb-4"
      dismissible
      @click:close="error = null"
    >
      {{ error }}
    </v-alert>

    <!-- Member Details -->
    <div v-else-if="member">
      <!-- Header Section -->
      <v-row class="mb-6">
        <v-col cols="12">
          <div class="d-flex align-center mb-4">
            <v-btn
              icon
              variant="text"
              @click="goBack"
              class="mr-3"
            >
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <h1 class="text-h4 font-weight-bold">Member Details</h1>
          </div>
        </v-col>
      </v-row>

      <!-- Member Profile Card -->
      <v-row>
        <v-col cols="12" md="4">
          <v-card class="pa-6 text-center" elevation="2">
            <div class="position-relative d-inline-block mb-4">
              <v-avatar
                :color="showMemberPhoto ? undefined : 'primary'"
                size="120"
                class="member-avatar"
              >
                <v-img
                  v-if="showMemberPhoto"
                  :src="memberPhotoUrl"
                  :alt="member.full_name"
                  @error="handleImageError"
                />
                <span v-else class="text-h3 font-weight-bold text-white">
                  {{ getInitials(member.full_name) }}
                </span>
              </v-avatar>
              
              <!-- Photo upload overlay -->
              <div v-if="canEdit" class="photo-upload-overlay">
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      size="small"
                      color="primary"
                      v-bind="props"
                      class="photo-upload-btn"
                    >
                      <v-icon>mdi-camera</v-icon>
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-item @click="triggerPhotoUpload">
                      <v-list-item-title>
                        <v-icon class="mr-2">mdi-upload</v-icon>
                        Upload Photo
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="showMemberPhoto" @click="deletePhoto">
                      <v-list-item-title>
                        <v-icon class="mr-2">mdi-delete</v-icon>
                        Delete Photo
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              
              <!-- Hidden file input -->
              <input
                ref="photoInput"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif"
                style="display: none"
                @change="handlePhotoUpload"
              />
            </div>

            <h2 class="text-h5 font-weight-bold mb-2">{{ member.full_name }}</h2>
            
            <v-chip
              :color="getRoleColor(member.position)"
              variant="flat"
              class="mb-3"
              size="large"
            >
              <v-icon start>{{ getRoleIcon(member.position) }}</v-icon>
              {{ member.position }}
            </v-chip>

            <div class="text-body-1 text-medium-emphasis mb-4">
              {{ member.department }}
            </div>

            <!-- Status Badge -->
            <v-chip
              :color="member.is_current ? 'success' : 'error'"
              variant="flat"
              size="small"
              class="mb-4"
            >
              <v-icon start>{{ member.is_current ? 'mdi-check-circle' : 'mdi-pause-circle' }}</v-icon>
              {{ member.is_current ? 'Active' : 'Inactive' }}
            </v-chip>

            <!-- Action Buttons -->
            <div class="d-flex flex-column gap-2" v-if="canEdit">
              <v-btn
                color="primary"
                variant="flat"
                @click="editMode = true"
                prepend-icon="mdi-pencil"
                block
              >
                Edit Member
              </v-btn>
              
              <v-btn
                :color="member.is_current ? 'warning' : 'success'"
                variant="outlined"
                @click="toggleMemberStatus"
                :prepend-icon="member.is_current ? 'mdi-pause' : 'mdi-play'"
                block
                :loading="statusLoading"
              >
                {{ member.is_current ? 'Deactivate' : 'Activate' }}
              </v-btn>

              <v-btn
                color="orange"
                variant="outlined"
                @click="showPasswordReset = true"
                prepend-icon="mdi-key-variant"
                block
              >
                Reset Password
              </v-btn>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="8">
          <!-- Contact Information -->
          <v-card class="mb-4" elevation="2">
            <v-card-title class="bg-blue-grey-lighten-5">
              <v-icon class="mr-2">mdi-card-account-details</v-icon>
              Contact Information
            </v-card-title>
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="mb-3">
                    <div class="text-caption text-medium-emphasis mb-1">Email</div>
                    <div class="text-body-1">
                      <v-icon class="mr-2" size="small">mdi-email</v-icon>
                      {{ member.email || 'Not provided' }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="mb-3">
                    <div class="text-caption text-medium-emphasis mb-1">Phone</div>
                    <div class="text-body-1">
                      <v-icon class="mr-2" size="small">mdi-phone</v-icon>
                      {{ member.phone || 'Not provided' }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Board Information -->
          <v-card class="mb-4" elevation="2">
            <v-card-title class="bg-blue-grey-lighten-5">
              <v-icon class="mr-2">mdi-account-group</v-icon>
              Board Information
            </v-card-title>
            <v-card-text class="pa-4">
              <v-row>
                <v-col cols="12" sm="6">
                  <div class="mb-3">
                    <div class="text-caption text-medium-emphasis mb-1">Start Date</div>
                    <div class="text-body-1">
                      <v-icon class="mr-2" size="small">mdi-calendar-start</v-icon>
                      {{ formatDate(member.start_date) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" v-if="member.end_date">
                  <div class="mb-3">
                    <div class="text-caption text-medium-emphasis mb-1">End Date</div>
                    <div class="text-body-1">
                      <v-icon class="mr-2" size="small">mdi-calendar-end</v-icon>
                      {{ formatDate(member.end_date) }}
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6">
                  <div class="mb-3">
                    <div class="text-caption text-medium-emphasis mb-1">Tenure</div>
                    <div class="text-body-1">
                      <v-icon class="mr-2" size="small">mdi-clock-outline</v-icon>
                      {{ calculateTenure(member.start_date, member.end_date) }}
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Biography -->
          <v-card class="mb-4" elevation="2">
            <v-card-title class="bg-blue-grey-lighten-5">
              <v-icon class="mr-2">mdi-text-account</v-icon>
              Biography
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="text-body-1" v-if="member.bio">
                {{ member.bio }}
              </div>
              <div class="text-body-2 text-medium-emphasis" v-else>
                No biography available.
              </div>
            </v-card-text>
          </v-card>

          <!-- Committees -->
          <v-card elevation="2">
            <v-card-title class="bg-blue-grey-lighten-5">
              <v-icon class="mr-2">mdi-account-supervisor</v-icon>
              Committee Memberships
            </v-card-title>
            <v-card-text class="pa-4">
              <div v-if="member.committees && member.committees.length > 0">
                <v-chip
                  v-for="committee in member.committees"
                  :key="committee.id"
                  class="mr-2 mb-2"
                  color="primary"
                  variant="outlined"
                >
                  {{ committee.name }}
                </v-chip>
              </div>
              <div class="text-body-2 text-medium-emphasis" v-else>
                No committee memberships.
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Edit Dialog -->
      <v-dialog v-model="editMode" max-width="800px" persistent>
        <v-card>
          <v-card-title class="text-h5">
            <v-icon class="mr-2">mdi-pencil</v-icon>
            Edit Member
          </v-card-title>
          
          <v-card-text>
            <v-form ref="editForm" v-model="editFormValid">
              <v-row>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editData.first_name"
                    label="First Name"
                    :rules="[rules.required]"
                    variant="outlined"
                    density="comfortable"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editData.last_name"
                    label="Last Name"
                    :rules="[rules.required]"
                    variant="outlined"
                    density="comfortable"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editData.email"
                    label="Email"
                    type="email"
                    :rules="[rules.required, rules.email]"
                    variant="outlined"
                    density="comfortable"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editData.phone"
                    label="Phone"
                    variant="outlined"
                    density="comfortable"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="editData.position"
                    :items="positionOptions"
                    label="Position"
                    :rules="[rules.required]"
                    variant="outlined"
                    density="comfortable"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="editData.department"
                    label="Department"
                    variant="outlined"
                    density="comfortable"
                  ></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-textarea
                    v-model="editData.bio"
                    label="Biography"
                    variant="outlined"
                    density="comfortable"
                    rows="4"
                  ></v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey"
              variant="text"
              @click="cancelEdit"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="saveChanges"
              :loading="saveLoading"
              :disabled="!editFormValid"
            >
              Save Changes
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Password Reset Dialog -->
      <v-dialog v-model="showPasswordReset" max-width="500px" persistent>
        <v-card>
          <v-card-title class="text-h5 bg-orange-lighten-4">
            <v-icon class="mr-2">mdi-key-variant</v-icon>
            Reset Password
          </v-card-title>
          <v-card-text class="pa-4">
            <v-alert 
              type="warning" 
              variant="tonal" 
              class="mb-4"
              icon="mdi-alert"
            >
              This will generate a new temporary password for {{ member?.first_name }} {{ member?.last_name }}.
              The user will need to change their password on next login.
            </v-alert>

            <v-form ref="passwordResetForm" v-model="passwordResetFormValid">
              <v-text-field
                v-model="newPassword"
                label="New Password"
                :type="showNewPassword ? 'text' : 'password'"
                :append-inner-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="showNewPassword = !showNewPassword"
                :rules="passwordRules"
                variant="outlined"
                class="mb-3"
                hint="Password must be at least 8 characters long"
                persistent-hint
              />

              <v-text-field
                v-model="confirmPassword"
                label="Confirm Password"
                :type="showConfirmPassword ? 'text' : 'password'"
                :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                @click:append-inner="showConfirmPassword = !showConfirmPassword"
                :rules="confirmPasswordRules"
                variant="outlined"
                hint="Re-enter the new password"
                persistent-hint
              />

              <v-checkbox
                v-model="requirePasswordChange"
                label="Require password change on next login"
                color="primary"
                class="mt-2"
              />
            </v-form>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              variant="text"
              @click="cancelPasswordReset"
            >
              Cancel
            </v-btn>
            <v-btn
              color="orange"
              variant="flat"
              @click="resetPassword"
              :loading="passwordResetLoading"
              :disabled="!passwordResetFormValid"
            >
              Reset Password
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>

    <!-- Member Not Found -->
    <div v-else class="text-center" style="min-height: 400px;">
      <v-icon size="64" color="grey" class="mb-4">mdi-account-question</v-icon>
      <h2 class="text-h5 text-medium-emphasis mb-2">Member Not Found</h2>
      <p class="text-body-1 text-medium-emphasis mb-4">
        The requested member could not be found.
      </p>
      <v-btn color="primary" @click="goBack">
        <v-icon start>mdi-arrow-left</v-icon>
        Back to Members
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useToast } from 'vue-toastification'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import api, { API_BASE_URL } from '@/services/api'

export default {
  name: 'MemberDetails',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    const toast = useToast()
    const { setBreadcrumbTitle } = useBreadcrumb()

    // API Base URL
    const apiBaseUrl = API_BASE_URL

    // Reactive data
    const loading = ref(true)
    const error = ref(null)
    const member = ref(null)
    const editMode = ref(false)
    const editFormValid = ref(false)
    const saveLoading = ref(false)
    const statusLoading = ref(false)
    const avatarUrl = ref(null)
    const photoInput = ref(null)
    const showMemberPhoto = ref(false)
    const memberPhotoUrl = ref('')

    // Password reset data
    const showPasswordReset = ref(false)
    const passwordResetFormValid = ref(false)
    const passwordResetLoading = ref(false)
    const newPassword = ref('')
    const confirmPassword = ref('')
    const showNewPassword = ref(false)
    const showConfirmPassword = ref(false)
    const requirePasswordChange = ref(true)

    // Edit form data
    const editData = reactive({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      bio: ''
    })

    // Form validation rules
    const rules = {
      required: value => !!value || 'Required.',
      email: value => {
        const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return pattern.test(value) || 'Invalid email.'
      }
    }

    // Password validation rules
    const passwordRules = [
      value => !!value || 'Password is required',
      value => (value && value.length >= 8) || 'Password must be at least 8 characters'
    ]

    const confirmPasswordRules = [
      value => !!value || 'Please confirm the password',
      value => value === newPassword.value || 'Passwords do not match'
    ]

    // Position options
    const positionOptions = [
      'Chairman',
      'Vice Chairman',
      'Secretary',
      'Treasurer',
      'Member'
    ]

    // Computed properties
    const canEdit = computed(() => {
      return authStore.user && (authStore.user.is_admin || (member.value && member.value.user_id === authStore.user.id))
    })

    // Photo handling methods
    const loadMemberPhoto = async (memberId) => {
      try {
        // Create a simple fetch request without authentication for public photo endpoint
        const response = await fetch(`${apiBaseUrl}/members/${memberId}/photo`, {
          method: 'HEAD'
        })
        
        if (response.ok) {
          // If successful (photo exists), set the photo URL
          memberPhotoUrl.value = `${apiBaseUrl}/members/${memberId}/photo?t=${Date.now()}`
          showMemberPhoto.value = true
          console.log('Photo exists, setting memberPhotoUrl to:', memberPhotoUrl.value)
        } else {
          // Photo doesn't exist
          showMemberPhoto.value = false
          memberPhotoUrl.value = ''
          console.log('No photo found, hiding member photo')
        }
      } catch (error) {
        // If photo doesn't exist (404) or other error, use initials
        showMemberPhoto.value = false
        memberPhotoUrl.value = ''
        console.log('Error loading photo, hiding member photo:', error)
      }
    }

    const triggerPhotoUpload = () => {
      photoInput.value?.click()
    }

    const handlePhotoUpload = async (event) => {
      const file = event.target.files[0]
      if (!file) return

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        toast.error('File size must be less than 5MB')
        return
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPG, PNG and GIF files are allowed')
        return
      }

      try {
        const formData = new FormData()
        formData.append('photo', file)

        await api.post(`/members/${member.value.id}/photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        // Reload the photo
        await loadMemberPhoto(member.value.id)
        toast.success('Photo uploaded successfully')
        
        // Clear the input
        event.target.value = ''
      } catch (error) {
        console.error('Error uploading photo:', error)
        const message = error.response?.data?.message || 'Failed to upload photo'
        toast.error(message)
      }
    }

    const deletePhoto = async () => {
      try {
        await api.delete(`/members/${member.value.id}/photo`)
        showMemberPhoto.value = false
        memberPhotoUrl.value = ''
        toast.success('Photo deleted successfully')
      } catch (error) {
        console.error('Error deleting photo:', error)
        const message = error.response?.data?.message || 'Failed to delete photo'
        toast.error(message)
      }
    }

    const handleImageError = (event) => {
      console.error('Image failed to load:', event?.target?.src || 'Unknown source')
      console.log('Current memberPhotoUrl:', memberPhotoUrl.value)
      console.log('Member avatar data:', member.value?.avatar)
      // Fallback to initials if image fails to load
      showMemberPhoto.value = false
      memberPhotoUrl.value = ''
    }

    // Methods
    const loadMember = async () => {
      loading.value = true
      error.value = null
      
      try {
        const memberId = route.params.id
        const response = await api.get(`/members/${memberId}`)
        member.value = response.data.member
        setBreadcrumbTitle(member.value.full_name)
        
        // Populate edit form with current data
        if (member.value.user) {
          editData.first_name = member.value.user.first_name || ''
          editData.last_name = member.value.user.last_name || ''
          editData.email = member.value.user.email || ''
          editData.phone = member.value.user.phone || ''
        }
        editData.position = member.value.position || ''
        editData.department = member.value.department || ''
        editData.bio = member.value.bio || ''
        
        // Load member photo
        await loadMemberPhoto(member.value.id)
        
      } catch (err) {
        console.error('Error loading member:', err)
        if (err.response?.status === 404) {
          error.value = 'Member not found'
        } else {
          error.value = 'Failed to load member details'
        }
      } finally {
        loading.value = false
      }
    }

    const goBack = () => {
      router.push('/members')
    }

    const getInitials = (name) => {
      if (!name || typeof name !== 'string') {
        return 'NA'
      }
      return name.split(' ').map(part => part[0]).join('').toUpperCase()
    }

    const getRoleColor = (role) => {
      const colors = {
        'Chairman': 'purple',
        'Vice Chairman': 'indigo',
        'Secretary': 'blue',
        'Treasurer': 'green',
        'Member': 'grey'
      }
      return colors[role] || 'grey'
    }

    const getRoleIcon = (role) => {
      const icons = {
        'Chairman': 'mdi-crown',
        'Vice Chairman': 'mdi-crown-outline',
        'Secretary': 'mdi-file-document-edit',
        'Treasurer': 'mdi-currency-usd',
        'Member': 'mdi-account'
      }
      return icons[role] || 'mdi-account'
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A'
      
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (e) {
        return 'Invalid date'
      }
    }

    const calculateTenure = (startDate, endDate) => {
      if (!startDate) return 'N/A'
      
      try {
        const start = new Date(startDate)
        const end = endDate ? new Date(endDate) : new Date()
        
        const diffTime = Math.abs(end - start)
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        const years = Math.floor(diffDays / 365)
        const months = Math.floor((diffDays % 365) / 30)
        
        if (years > 0) {
          return `${years} year${years > 1 ? 's' : ''}, ${months} month${months > 1 ? 's' : ''}`
        } else if (months > 0) {
          return `${months} month${months > 1 ? 's' : ''}`
        } else {
          return `${diffDays} day${diffDays > 1 ? 's' : ''}`
        }
      } catch (e) {
        return 'N/A'
      }
    }

    const toggleMemberStatus = async () => {
      statusLoading.value = true
      
      try {
        const endpoint = member.value.is_current ? 'deactivate' : 'activate'
        await api.put(`/members/${member.value.id}/${endpoint}`)
        
        member.value.is_current = !member.value.is_current
        toast.success(`Member ${member.value.is_current ? 'activated' : 'deactivated'} successfully`)
        
      } catch (err) {
        console.error('Error updating member status:', err)
        toast.error('Failed to update member status')
      } finally {
        statusLoading.value = false
      }
    }

    const cancelEdit = () => {
      editMode.value = false
      // Reset form data
      if (member.value.user) {
        editData.first_name = member.value.user.first_name || ''
        editData.last_name = member.value.user.last_name || ''
        editData.email = member.value.user.email || ''
        editData.phone = member.value.user.phone || ''
      }
      editData.position = member.value.position || ''
      editData.department = member.value.department || ''
      editData.bio = member.value.bio || ''
    }

    const saveChanges = async () => {
      saveLoading.value = true
      
      try {
        const updateData = {
          user: {
            first_name: editData.first_name,
            last_name: editData.last_name,
            email: editData.email,
            phone: editData.phone
          },
          position: editData.position,
          department: editData.department,
          bio: editData.bio
        }
        
        const response = await api.put(`/members/${member.value.id}`, updateData)
        member.value = response.data.member
        
        editMode.value = false
        toast.success('Member updated successfully')
        
      } catch (err) {
        console.error('Error updating member:', err)
        toast.error('Failed to update member')
      } finally {
        saveLoading.value = false
      }
    }

    // Password reset methods
    const cancelPasswordReset = () => {
      showPasswordReset.value = false
      newPassword.value = ''
      confirmPassword.value = ''
      showNewPassword.value = false
      showConfirmPassword.value = false
      requirePasswordChange.value = true
    }

    const resetPassword = async () => {
      passwordResetLoading.value = true
      
      try {
        const resetData = {
          new_password: newPassword.value,
          require_password_change: requirePasswordChange.value
        }
        
        const response = await api.put(`/users/${member.value.user_id}/reset-password`, resetData)
        
        cancelPasswordReset()
        toast.success('Password reset successfully. User will receive notification.')
        
      } catch (err) {
        console.error('Error resetting password:', err)
        const message = err.response?.data?.message || 'Failed to reset password'
        toast.error(message)
      } finally {
        passwordResetLoading.value = false
      }
    }

    // Watch for route changes
    watch(() => route.params.id, () => {
      if (route.params.id) {
        loadMember()
      }
    })

    // Initialize
    onMounted(() => {
      loadMember()
    })

    return {
      // Data
      loading,
      error,
      member,
      editMode,
      editFormValid,
      saveLoading,
      statusLoading,
      editData,
      rules,
      positionOptions,
      avatarUrl,
      photoInput,
      showMemberPhoto,
      memberPhotoUrl,
      
      // Password reset data
      showPasswordReset,
      passwordResetFormValid,
      passwordResetLoading,
      newPassword,
      confirmPassword,
      showNewPassword,
      showConfirmPassword,
      requirePasswordChange,
      passwordRules,
      confirmPasswordRules,
      
      // Computed
      canEdit,
      
      // Methods
      loadMember,
      goBack,
      getInitials,
      getRoleColor,
      getRoleIcon,
      formatDate,
      calculateTenure,
      toggleMemberStatus,
      cancelEdit,
      saveChanges,
      cancelPasswordReset,
      resetPassword,
      
      // Photo methods
      loadMemberPhoto,
      triggerPhotoUpload,
      handlePhotoUpload,
      deletePhoto,
      handleImageError
    }
  }
}
</script>

<style scoped>
.member-avatar {
  border: 4px solid white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.gap-2 > * + * {
  margin-top: 8px;
}

.photo-upload-overlay {
  position: absolute;
  bottom: 0;
  right: 0;
}

.photo-upload-btn {
  background-color: white !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.photo-upload-overlay .v-btn {
  min-width: 32px !important;
  width: 32px;
  height: 32px;
}
</style>